import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { Fragment } from 'react';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { isActiveAppearance } from '@sorare/core/src/lib/players';

import { CompetitionHeader } from '#src/components/importLineup/ImportLineupContent/CompetitionHeader/index.tsx';
import { LineupItem } from '#src/components/importLineup/ImportLineupContent/LineupItem/index.tsx';
import { So5LeaderboardGroupWrapper } from '#src/components/importLineup/ImportLineupContent/So5LeaderboardGroupWrapper/index.tsx';
import { useHierarchiedLineups } from '#src/components/importLineup/ImportLineupContent/useHierarchiedLineups.ts';

import type { DeletedLineups_so5Lineup } from './__generated__/index.graphql.ts';

type Props = {
  cancelledSo5Lineups: DeletedLineups_so5Lineup[];
};

export const DeletedLineups = ({ cancelledSo5Lineups }: Props) => {
  const hierarchiedLineups = useHierarchiedLineups(cancelledSo5Lineups);
  return (
    <Vertical gap={2}>
      {hierarchiedLineups.map(({ competitions, so5LeaderboardGroup }) => (
        <So5LeaderboardGroupWrapper
          key={so5LeaderboardGroup.slug}
          so5LeaderboardGroupInterface={so5LeaderboardGroup}
        >
          {competitions?.map(({ mainRarityType, seasonality, lineups }) => (
            <Fragment
              key={`${so5LeaderboardGroup.slug}-${mainRarityType}-${seasonality}`}
            >
              <CompetitionHeader
                rarity={mainRarityType}
                seasonality={seasonality}
                count={lineups.length}
              />
              {lineups.map(({ id, so5Leaderboard, so5Appearances }) => (
                <LineupItem
                  key={id}
                  iconUrl={
                    so5Leaderboard?.divisionIconUrl ||
                    so5Leaderboard?.svgLogoUrl ||
                    so5Leaderboard?.iconUrl
                  }
                  rarity={so5Leaderboard?.mainRarityType}
                  appearances={so5Appearances
                    .filter(isActiveAppearance)
                    .map(a => ({
                      ...a,
                      avatarPictureUrl: a.anyPlayer.avatarPictureUrl,
                      playerDisplayName: a.anyPlayer.displayName,
                    }))}
                />
              ))}
            </Fragment>
          ))}
        </So5LeaderboardGroupWrapper>
      ))}
    </Vertical>
  );
};

DeletedLineups.fragments = {
  so5Lineup: gql`
    fragment DeletedLineups_so5Lineup on So5Lineup {
      id
      so5Appearances(includeSubs: true) {
        substitutionState
        id
        index
        captain
        anyPlayer {
          slug
          avatarPictureUrl
          displayName
        }
      }
      so5Leaderboard {
        slug
        mainRarityType
        seasonality
        svgLogoUrl
        iconUrl
        divisionIconUrl
        so5LeaderboardGroup {
          slug
          ...So5LeaderboardGroupWrapper_so5LeaderboardGroupInterface
        }
      }
      ...useHierarchiedSo5Lineups_so5Lineup
    }
    ${useHierarchiedLineups.fragments.so5Lineup}
    ${So5LeaderboardGroupWrapper.fragments.so5LeaderboardGroupInterface}
  ` as TypedDocumentNode<DeletedLineups_so5Lineup>,
};
